import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { FlatList, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StackNavigationProp } from '@react-navigation/stack'
import { getTimeZone } from 'react-native-localize'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NavigationContainer } from '@src/screens/Common/containers'
import {
  EhrAppointmentKind,
  EhrAppointmentProviderKind,
  EhrGetHealthieInstanceKind,
  EhrSlot,
  UiInlineAlert,
} from '@src/types'
import { userSelector } from '@src/selectors/app'
import { NavigatorContext, useGoBack, useSnack } from '@src/utils'
import { ErrorMessages } from '@src/config'
import {
  AppStackParamList,
  AuthStackParamList,
  CommonAuthorizedStackParamList,
  CommonStackParamList,
  OnboardingStackParamsList,
} from '@src/navigation/types'
import { InlineAlert } from '@src/components'
import { Text } from '@src/components/base'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { EhrAvailableDatesWithSlotsQuery } from '../graphql/fetchEhrAvailableDatesWithSlots.generated'
import { EhrBookAppointmentMutation } from '../graphql/bookAppointment.generated'
import { NUTRITIONIST_NOT_LICENSED_ERROR, SLOT_TAKEN_ERROR } from '../constants'
import { DatePicker } from './DatePicker'
import { LoadingTimeSlot } from './CallScheduling/LoadingTimeSlot'
import { TimeSlot } from './CallScheduling/TimeSlot'
import { CoachInfoCardContent } from './CoachInfo/CoachInfoCardContent'

const renderLoadingItem = () => <LoadingTimeSlot />

const APPOINTMENT_KIND = EhrAppointmentKind.Initial_60

export const ReferralVideoCall = () => {
  const styles = useStyleSheet(themedStyles)

  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const authStackNavigation = useNavigation<StackNavigationProp<AuthStackParamList>>()
  const appStackNavigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const commonNavigation = useNavigation<StackNavigationProp<CommonStackParamList>>()
  const authorizedStackNavigation = useNavigation<
    StackNavigationProp<CommonAuthorizedStackParamList>
  >()
  const route = useRoute<
    | RouteProp<AuthStackParamList, 'ReferralVideoCall'>
    | RouteProp<AppStackParamList, 'ReferralVideoCall'>
    | RouteProp<OnboardingStackParamsList, 'ReferralVideoCall'>
  >()
  const insets = useSafeAreaInsets()
  const goBack = useGoBack()
  const navigation = useNavigation()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const bookingRef = useRef(false)

  const { coachUserId, referredByUserId, status } = route.params

  const [ehrAvailableDatesWithSlots, setEhrAvailableDatesWithSlots] = useState<
    EhrAvailableDatesWithSlotsQuery['ehrAvailableDatesWithSlots'] | null
  >(null)

  const availableDates =
    ehrAvailableDatesWithSlots?.dates.map((item) => item.date).sort((a, b) => a.localeCompare(b)) ||
    []

  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<EhrSlot | null>(null)

  const [loading, setLoading] = useState<boolean>(true)

  const showSnack = useSnack()

  const timeZone = user?.timeZone || getTimeZone()

  useEffect(() => {
    dispatch({
      type: 'nutritionistHub/fetchEhrAvailableDatesWithSlots',
      payload: {
        appointmentKind: APPOINTMENT_KIND,
        providerKind: EhrAppointmentProviderKind.Coach,
        getHealthieInstanceKind: EhrGetHealthieInstanceKind.BerryStreet,
        timeZone,
        providerId: coachUserId,
        excludedProvidersHealthieIds: [],
      },
      success: (response: EhrAvailableDatesWithSlotsQuery) => {
        setEhrAvailableDatesWithSlots(response.ehrAvailableDatesWithSlots)
        const availableDatesArray = response.ehrAvailableDatesWithSlots.dates
          .map((item) => item.date)
          .sort((a, b) => a.localeCompare(b))
        if (availableDatesArray.length > 0) {
          setSelectedDate(availableDatesArray[0])
        }
        setLoading(false)
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
        if (navigation.canGoBack()) {
          goBack()
        }
        setLoading(false)
      },
    })
  }, [coachUserId, dispatch, goBack, navigation, showSnack, timeZone])

  useEffect(
    () => () => {
      if (bookingRef.current) {
        disableLoader()
        bookingRef.current = false
      }
    },
    [disableLoader],
  )

  const provider = ehrAvailableDatesWithSlots?.providers.find(
    (provider) => 'coachId' in provider && provider.coachId === coachUserId,
  )

  const confirmSlot = () => {
    if (!selectedSlot || !provider || !('coachId' in provider)) {
      return
    }

    if (!user) {
      authStackNavigation.navigate('ReferralVideoCallReserved', {
        meetingAt: selectedSlot.time,
        coachFullName: provider.fullName,
        coachPhoto: provider.photo,
        coachCredentials: provider.credentials,
        coachBio: provider.bio,
        coachSpecialties: provider.specialties,
        coachId: selectedSlot.providerId,
        coachUserId,
        referringUserId: referredByUserId,
      })
      return
    }

    if (bookingRef.current) {
      return
    }

    bookingRef.current = true
    enableLoader()

    dispatch({
      type: 'nutritionistHub/bookAppointment',
      payload: {
        appointmentKind: APPOINTMENT_KIND,
        timeZone,
        date: selectedSlot.time,
        coachId: selectedSlot.providerId,
        referringUserId: referredByUserId,
      },
      success: (response: EhrBookAppointmentMutation['ehrBookAppointment']) => {
        dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })
        dispatch({ type: 'nutritionistHub/fetchEhrSuggestedAvailability' })

        if (response.meetingAt) {
          authorizedStackNavigation.replace('VideoCallConfirmed', {
            meetingAt: response.meetingAt,
            coachFullName: provider.fullName,
            coachPhoto: provider.photo,
            coachCredentials: provider.credentials,
            coachBio: provider.bio,
            coachSpecialties: provider.specialties,
            info:
              status === 'coach_not_licensed'
                ? 'We booked you with another nutritionist that is licensed in your state.'
                : undefined,
          })

          dispatch({ type: 'uiFlags/fetch' })
          dispatch({ type: 'users/fetch' })
          return
        }

        if (navigation.canGoBack()) {
          goBack()
        }
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        if (errorMessage.includes(SLOT_TAKEN_ERROR)) {
          appStackNavigation.replace('ReferralVideoCall', {
            coachUserId,
            referredByUserId,
            status: 'slot_not_available',
          })
          return
        }

        if (errorMessage.includes(NUTRITIONIST_NOT_LICENSED_ERROR)) {
          appStackNavigation.replace('ReferralVideoCallNotLicensed', {
            meetingAt: selectedSlot.time,
          })
          return
        }

        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        disableLoader()
        bookingRef.current = false
      },
    })
  }

  const renderSlotItem = ({ item: slot }: { item: EhrSlot }) => {
    return (
      <TimeSlot
        onSelect={setSelectedSlot}
        onConfirm={confirmSlot}
        slot={slot}
        isSelected={slot.time === selectedSlot?.time}
      />
    )
  }

  const availableSlots = ehrAvailableDatesWithSlots?.dates.find(
    (date) => date.date === selectedDate,
  )?.slots

  useLazyScreenPreload('CoachInfoModal')
  useLazyScreenPreload('VideoCallConfirmed')

  const openBio = () => {
    if (!provider || !('coachId' in provider)) {
      return
    }

    const { photo, fullName, credentials, bio, specialties } = provider

    commonNavigation.navigate('CoachInfoModal', {
      variant: 'bio',
      photo,
      fullName,
      credentials,
      bio,
      specialties,
    })
  }

  const getTitle = () => {
    if (status === 'slot_not_available') {
      return 'Your selected date and time is no longer available. Please select a different time.'
    }

    if (status === 'coach_not_licensed') {
      return (
        'Please select appointment time with the nutritionist below as ' +
        'the previous nutritionist is not licensed in your state.'
      )
    }

    return 'Welcome! You’ve been invited to book a nutritionist call.'
  }

  return (
    <NavigationContainer
      title="Schedule Call"
      goBack={navigation.canGoBack() ? goBack : undefined}
      style={styles.container}
    >
      <Text type="large" style={styles.title}>
        {getTitle()}
      </Text>
      {provider && 'coachId' in provider && (
        <CoachInfoCardContent
          openBio={openBio}
          fullName={provider.fullName}
          bio={provider.bio}
          photo={provider.photo}
          credentials={provider.credentials}
          style={styles.nutritionistInfoCard}
        />
      )}
      <DatePicker
        onDateSelect={setSelectedDate}
        selectedDate={selectedDate}
        dates={availableDates}
      />

      <View style={styles.content}>
        <InlineAlert
          style={styles.alert}
          category={UiInlineAlert.Info}
          message={`Timezone: ${timeZone}`}
        />

        <View style={styles.slotsHeader}>
          <Text type="regular">Available Slots</Text>
        </View>

        <FlatList
          style={styles.slotsList}
          data={loading ? Array(3).fill(0) : availableSlots}
          renderItem={loading ? renderLoadingItem : renderSlotItem}
          contentContainerStyle={{ paddingBottom: insets.bottom, gap: 8 }}
          showsVerticalScrollIndicator={false}
        />
      </View>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  content: {
    flex: 1,
    backgroundColor: 'theme.background',
    paddingHorizontal: 16,
  },
  container: {
    backgroundColor: 'theme.background',
    flex: 1,
  },
  title: {
    textAlign: 'center',
    marginHorizontal: 24,
  },
  nutritionistInfoCard: {
    marginTop: 24,
    marginHorizontal: 16,
    borderColor: 'theme.surface.base2',
  },
  alert: {
    marginVertical: 16,
  },
  slotsList: {
    flex: 1,
    marginTop: 16,
  },
  slotsHeader: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
  },
})
